// shared/src/prompts/m-consensus-skeptic.ts
// Chain V2 — market consensus path, skeptic stage (CROSS-FED).
// Symmetric mirror of m-contrarian-skeptic. Attacks the consensus draft using
// the contrarian draft as structural steel-man counter.
//
// Per design-notes L7/L14/L28.

import { z } from 'zod';
import { SignalConfidenceBucketEnum } from '../schemas/signal.js';
import { coercedEnum, coerceCiteTag } from '../resolvers/llm-enum-coerce.js';
import { tolerantRowArray } from '../resolvers/llm-row-drop.js';
import { llmStringItem } from '../resolvers/llm-string-coerce.js';
import { renderWorldStateGrounding, type WorldStateGrounding } from './_grounding.js';
import { renderNewsBlock } from './_news-render.js';
import type { LlmNewsItem } from '../news/types.js';

export const VERSION = '1.3.0';   // 2026-08-07 — disconfirming_evidence per-ROW omission hardening (tolerantRowArray: drop malformed rows). 2026-07-10 — disconfirming_evidence + cross_fed_counter .default([]) (omission-safe; empty is the valid D39 value). 2026-05-25 — add 'news' to cite enum
export const STEP    = 'm.consensus.skeptic';
export const MODEL   = 'deepseek-v4-flash';
export const MAX_TOKENS = 32000;   // 2026-08-01 blanket truncation-proofing (was 12000)
export const ROLE    = 'skeptic' as const;
export const DESC    = 'Red-teams the consensus draft using the contrarian draft as steel-man counter' as const;   // #45 registry — one-line operator description

export const OutputSchema = z.object({
  // D39 (2026-05-28): dropped .min(1) — let skeptic emit empty when consensus draft is genuinely unimpeachable.
  // 2026-08-07: per-ROW omission hardening — a present evidence row that dropped
  // `point` or whose `cite` is unrecoverable is DROPPED + logged (not a brief kill).
  disconfirming_evidence: tolerantRowArray(z.object({
    cite:  coercedEnum([
      'macro', 'analyst_output', 'observable', 'opposing_draft',
      'news', 'factual_error', 'reasonability',
    ], coerceCiteTag),
    point: z.string().min(1),
  }), 'm.consensus.skeptic.disconfirming_evidence'),   // 2026-07-10: omitted WHOLE key still defaults to [] (D39 valid-empty)
  cross_fed_counter: z.array(llmStringItem).default([]),   // 2026-07-10: same omission-fragility class — empty = no opposing-draft counters
  conviction:        SignalConfidenceBucketEnum,
  reasoning:         z.string(),
}).passthrough();
export type Output = z.infer<typeof OutputSchema>;

export const SYSTEM = `You are the SKEPTIC challenging the CONSENSUS analyst's draft on a market-level macro thesis. Your role is the structural counterweight to the consensus — surface evidence that the prevailing view is wrong, the desk is offside, or the supporting payload contradicts the narrative.

## CROSS-FED STEEL-MAN
The user message contains the CONTRARIAN analyst's draft (independently produced in parallel) as your structural steel-man counter-evidence. You do NOT need to invent counter-arguments — the contrarian analyst already made them. Your job is to:
  1. READ the consensus draft.
  2. READ the contrarian draft for the strongest disconfirming points the consensus failed to address.
  3. AUDIT the consensus against payload, GROUNDING factual checks, observable plausibility.
  4. ATTACK the consensus using the sharpest weapon.

## ROLE BOUNDARIES
- READ consensus's draft. Find the cracks.
- USE contrarian draft (OPPOSING analyst's parallel output) as steel-man source material.
- USE MACRO + NEWS contradictions, FACTUAL CHECKS, OBSERVABLE plausibility.
- NO calibration. NO historical pattern matching. THIS BRIEF, THIS PAYLOAD, THIS DRAFT.

## INPUTS YOU RECEIVE (user message)
- macro              (current macro payload)
- own_draft          (consensus.analyst output — challenge this)
- opposing_draft     (contrarian.analyst output — your steel-man counter)
- news               (last 48h macro)

## OUTPUT SCHEMA (strict JSON; first char='{'; last char='}'; no commentary)
{
  "disconfirming_evidence": [
    { "cite":  "macro" | "analyst_output" | "observable" | "opposing_draft" | "news" | "factual_error" | "reasonability",
      "point": "<≤25w specific challenge>" }
  ],
  "cross_fed_counter": [
    "<short ≤20w summary of each contrarian-draft point you cited — audit trail>"
  ],
  "conviction": "HIGH" | "MED" | "LOW",
  "reasoning":  "<headline ≤15w. Then 1-2 sentences (≤50w total) threading the disconfirming evidence.>"
}

## MANDATORY CROSS-CHECK PASS (factual_error)
(a) basis.catalyst, basis.invalidation, conditional referencing dates < TODAY as forthcoming → cite='factual_error'.
(b) Current-state numbers in thesis_paragraph / basis.evidence contradicting MACRO payload → cite='factual_error'.

## MANDATORY REASONABILITY PASS
(c) Magnitude vs vol: predicted change_pct > 2× hist-vol baseline for asset class over horizon.
(d) Catalyst-vs-window: post_catalyst_window_days < 3 for slow-developing macro thesis.
(e) Crowded-consensus contradiction: if priced_explanation='crowded_consensus' but consensus is predicting a CONTINUATION of the crowded trade, surface — crowded trades are typically reversal candidates not continuation candidates. emit reasonability cite.

## CROSS-FED STEEL-MAN CITES (cite='opposing_draft')
For every cross_fed_counter entry, also emit a disconfirming_evidence entry with cite='opposing_draft'. Reference SPECIFIC points from the contrarian draft — basis.evidence, priced_explanation enum value, macro_predictions divergence on a specific indicator, etc. Format: "<consensus-field> vs <contrarian-field>: <specific divergence>". Example: "priced_explanation='structural_flow' for crypto rally, but contrarian.basis.evidence cites m2_yoy_pct=8.5% slowing — flow argument depends on liquidity that's actively contracting."

## RULES
1. disconfirming_evidence: emit honest challenges. Empty array OK when target is genuinely unimpeachable (D39 — no forced ≥1).
2. Every opposing_draft cite MUST appear in cross_fed_counter.
3. NO calibration cites. THIS BRIEF only.
4. conviction default MED. HIGH when ≥2 factual_error OR ≥3 opposing_draft cites the consensus failed to address. LOW when contrarian mostly agrees with consensus framing.
5. NO restating the consensus thesis. Delta-only.
6. NO new fact invention.
7. NO defensive hedging.
8. factual_error cites are HARD findings — emit conviction at least MED when any factual_error surfaces.
9. STEEL-MAN AWARENESS. The contrarian draft you receive is a real attempt at the opposing view — treat the strongest contrarian points as serious challenges to consensus, not strawmen.
`;

export interface MConsensusSkepticInput {
  readonly macro:           Record<string, unknown>;
  readonly own_draft:       Record<string, unknown>;
  readonly opposing_draft:  Record<string, unknown>;
  readonly grounding?:      WorldStateGrounding;
  readonly news?:           ReadonlyArray<LlmNewsItem>;
}

function fmt(v: unknown): string {
  if (v === null || v === undefined) return 'null';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

function draftExcerpt(d: Record<string, unknown> | undefined, label: string): string {
  if (!d || Object.keys(d).length === 0) return `  (no ${label} draft provided)`;
  const fields = [
    'thesis_headline', 'basis', 'thesis_paragraph', 'priced_explanation',
    'probability', 'horizon', 'conditional', 'regime', 'regime_confidence',
    'thesis_div',
  ];
  const lines = fields
    .filter((f) => f in d)
    .map((f) => `  ${f}: ${fmt(d[f])}`);
  const predictions = d['macro_predictions'];
  if (Array.isArray(predictions) && predictions.length > 0) {
    const predLines = predictions.slice(0, 5).map((p, i) => {
      const pred = p as Record<string, unknown>;
      const mode = String(pred['prediction_mode'] ?? '?');
      const thesis = String(pred['thesis_narrative'] ?? '').slice(0, 60);
      const obs = Array.isArray(pred['predicted_observables']) ? pred['predicted_observables'] : [];
      const obsBrief = obs.slice(0, 4).map((o) => {
        const oo = o as Record<string, unknown>;
        return `${String(oo['indicator'] ?? '?')}:${String(oo['direction'] ?? '?')}${String(oo['change_pct'] ?? '?')}%`;
      }).join(', ');
      return `    [${i + 1}] ${mode} · "${thesis}..." · obs=${obsBrief}`;
    });
    lines.push('  macro_predictions:');
    lines.push(...predLines);
  }
  return lines.join('\n');
}

export function buildUserPrompt(input: MConsensusSkepticInput): string {
  const groundingBlock = input.grounding ? renderWorldStateGrounding(input.grounding) + '\n' : '';
  const macroLines = Object.entries(input.macro ?? {})
    .filter(([k]) => typeof k === 'string' && !k.startsWith('_'))
    .slice(0, 30)
    .map(([k, v]) => `  ${k}: ${fmt(v)}`).join('\n');

  return `${groundingBlock}MACRO PAYLOAD (input to consensus.analyst):
${macroLines || '  (empty)'}

OWN_DRAFT (consensus.analyst output — challenge this):
${draftExcerpt(input.own_draft, 'consensus')}

OPPOSING_DRAFT (contrarian.analyst output — your structural steel-man counter):
${draftExcerpt(input.opposing_draft, 'contrarian')}

MACRO NEWS (last 48h):
${renderNewsBlock(input.news ?? [])}

REQUEST: emit_m_consensus_skeptic

Emit a SINGLE JSON object matching the schema. Run MANDATORY CROSS-CHECK + REASONABILITY passes first using GROUNDING FACTS, then cross-fed steel-man attacks using OPPOSING_DRAFT. disconfirming_evidence may be empty (D39 — no forced ≥1). Every opposing_draft cite MUST appear in cross_fed_counter.`;
}
